import { useEffect, useRef, useState } from 'react';
import { INTRO_SEEN_KEY } from '../../App';

interface PreloaderProps {
    onLoadingComplete: () => void;
}

/**
 * Release the hero and navbar, and remember that the intro has run. Called once,
 * whether the loader completed normally or was unmounted early — leaving
 * `__cgPreloading` true is what strands the hero on a later visit to Home.
 */
function handOff() {
    window.__cgPreloading = false;
    window.dispatchEvent(new Event('cg:reveal-hero'));
    try {
        window.localStorage.setItem(INTRO_SEEN_KEY, '1');
    } catch {
        // Storage blocked — the intro simply plays again next time.
    }
}

const RADIUS = 40;
const STROKE = 1;

/**
 * Black-screen preloader: a single thin, static ring with the live percentage
 * counting up at its centre — nothing else (no logo, no progress arc), matching
 * the proposed landing-page video exactly. Once it reaches 100%, the whole
 * loader fades out (ease-out) and cues the hero's layered reveal.
 */
export default function Preloader({ onLoadingComplete }: PreloaderProps) {
    const [progress, setProgress] = useState(0);
    const [exiting, setExiting] = useState(false);
    const handedOff = useRef(false);

    // Count up to 100 — ~2s fill (50 ticks × 40ms). The updater only bumps state;
    // the hand-off runs from a commit-time effect below (never during render).
    useEffect(() => {
        const timer = setInterval(() => {
            setProgress((prev) => Math.min(prev + 2, 100));
        }, 40);
        return () => {
            clearInterval(timer);
            // Unmounted early: still release the hero, or it waits forever.
            if (!handedOff.current) {
                handedOff.current = true;
                handOff();
            }
        };
    }, []);

    // At 100%, cue the hero's layered reveal and fade the loader out over 600ms.
    useEffect(() => {
        if (progress < 100 || handedOff.current) return;
        handedOff.current = true;
        setExiting(true);
        handOff();
        const t = setTimeout(onLoadingComplete, 650);
        return () => clearTimeout(t);
    }, [progress, onLoadingComplete]);

    const size = (RADIUS + STROKE) * 2;

    return (
        <div
            className={`fixed inset-0 z-9999 flex items-center justify-center bg-black transition-opacity duration-[600ms] ease-out ${
                exiting ? 'pointer-events-none opacity-0' : 'opacity-100'
            }`}
        >
            <div className="relative grid place-items-center">
                <svg width={size} height={size} aria-hidden="true">
                    <circle
                        cx={RADIUS + STROKE}
                        cy={RADIUS + STROKE}
                        r={RADIUS}
                        fill="none"
                        stroke="rgba(255,255,255,0.32)"
                        strokeWidth={STROKE}
                    />
                </svg>
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center font-primary text-[16px] font-semibold tabular-nums leading-none text-white">
                    {progress}%
                </span>
            </div>
        </div>
    );
}
